import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Button } from '@/components/ui/button';
import { isNative } from '@/native/platform';
import {
  evaluateUpdateGate,
  fetchAppVersionManifest,
  shouldCheckNow,
  type UpdateGateStatus,
} from '@/lib/update-gate';

// Runs the forced-update check on native only (the web deploy has no store
// gate to enforce). Checked once at launch and again on resume, throttled to
// at most once per 6h — see shouldCheckNow.
function useUpdateGateStatus(): UpdateGateStatus {
  const [status, setStatus] = useState<UpdateGateStatus>({ kind: 'none' });

  useEffect(() => {
    if (!isNative()) return;

    let disposed = false;

    const check = async () => {
      if (!shouldCheckNow()) return;
      const manifest = await fetchAppVersionManifest();
      if (!manifest || disposed) return;
      try {
        const info = await CapacitorApp.getInfo();
        if (disposed) return;
        setStatus(evaluateUpdateGate(info.version, manifest));
      } catch {
        // getInfo failing is a platform quirk, not a reason to block anything.
      }
    };

    void check();
    const listener = CapacitorApp.addListener('resume', () => void check());

    return () => {
      disposed = true;
      void listener.then((l) => l.remove());
    };
  }, []);

  return status;
}

function openStore(manifest: { store_url_android: string; store_url_ios: string }) {
  const platform = Capacitor.getPlatform();
  const url = platform === 'ios' ? manifest.store_url_ios : manifest.store_url_android;
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}

function RequiredUpdateScreen({ status }: { status: Extract<UpdateGateStatus, { kind: 'required' }> }) {
  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-background px-6 text-center">
      <h1 className="text-2xl font-semibold text-foreground">Update required</h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        {status.manifest.message || 'A new version of MindCouncil is required to continue. Please update to keep using the app.'}
      </p>
      <Button size="lg" onClick={() => openStore(status.manifest)}>
        Update now
      </Button>
    </div>
  );
}

function RecommendedUpdateBanner({
  status,
  onDismiss,
}: {
  status: Extract<UpdateGateStatus, { kind: 'recommended' }>;
  onDismiss: () => void;
}) {
  return (
    <div className="fixed inset-x-0 top-0 z-[9998] flex items-center justify-between gap-3 bg-primary px-4 py-2 text-sm text-primary-foreground">
      <span className="truncate">{status.manifest.message || 'A new version of MindCouncil is available.'}</span>
      <div className="flex shrink-0 items-center gap-2">
        <button className="underline underline-offset-2" onClick={() => openStore(status.manifest)}>
          Update
        </button>
        <button aria-label="Dismiss" className="opacity-80 hover:opacity-100" onClick={onDismiss}>
          ✕
        </button>
      </div>
    </div>
  );
}

/** Mounted once at the app root, inside <App />. Renders nothing on web or when up to date. */
export function UpdateGate({ children }: { children: React.ReactNode }) {
  const status = useUpdateGateStatus();
  const [dismissed, setDismissed] = useState(false);

  if (status.kind === 'required') {
    return <RequiredUpdateScreen status={status} />;
  }

  return (
    <>
      {status.kind === 'recommended' && !dismissed && (
        <RecommendedUpdateBanner status={status} onDismiss={() => setDismissed(true)} />
      )}
      {children}
    </>
  );
}
